import { useRef } from 'react';
import { Link } from 'react-router-dom';
import type { MediaRef } from '@astrally/shared';
import { MEDIA_VIDEO_CONTENT_TYPES } from '@astrally/shared';
import { MEDIA_IMAGE_CONTENT_TYPES, VIDEO_MAX_MS } from '../../lib/media';
import { useI18n } from '../../i18n';
import type { PreparedUpload } from '../../lib/backend/types';

/**
 * The attachment half of the chat composer.
 *
 * Split out of ConversationView because the composer had one job and now has
 * three (write, attach, disclose), not because the UI is complicated. All the
 * state lives in ConversationView, which owns the send; this file is the
 * presentation and the file inputs.
 *
 * THREE THINGS ARE SAID BEFORE A FILE IS EVER PICKED, and that ordering is the
 * whole design:
 *
 *  1. THE PRO GATE. A non-Pro account is never shown a file picker — there is
 *     no `<input type="file">` in that branch at all, so there is nothing to
 *     tap by accident and nothing to be refused after the work of choosing,
 *     resizing and uploading is already spent. Learning about a paywall at the
 *     end is the most annoying possible order to learn it in.
 *
 *  2. VIDEO IS NOT CHECKED. Nothing inspects a clip — the moderation provider
 *     takes text and images, and we do not sample one frame out of fifteen
 *     seconds to manufacture coverage. The sender is told that before they
 *     choose, not in a footnote afterwards.
 *
 *  3. VIEWING IS PRO-ONLY TOO. A photo sent to a free account lands as a
 *     blurred placeholder they cannot open. The server answers that question
 *     for certain once the message is sent (see ConversationView), but the
 *     sender is warned of the possibility BEFORE they attach anything —
 *     because the alternative is finding out after the photo is already gone.
 */

export interface ChatAttachment {
  prepared: PreparedUpload;
  /** Object URL of the resized image. Null for video — there is no cheap thumbnail. */
  previewUrl: string | null;
  /**
   * Set once these bytes have reached Storage and been given a moderation
   * receipt. A send can fail AFTER the upload succeeded — moderation refusing
   * the caption is the common case — and re-uploading up to 6MB of video
   * because someone reworded a sentence would spend the user's data twice and
   * leave an orphaned object behind. The receipt stays valid, so a retry reuses
   * this.
   */
  uploaded?: MediaRef;
}

/** The paperclip. Never disabled for tier — only while a file is being prepared. */
export function ChatAttachButton({
  open,
  busy,
  onToggle,
}: {
  open: boolean;
  busy: boolean;
  onToggle(): void;
}) {
  const { t } = useI18n();
  return (
    <button
      className="btn btn--ghost"
      aria-label={t('msg.media.attach')}
      aria-expanded={open}
      // In-flight protection only: a second tap mid-resize would reopen the
      // picker over a file that is still being encoded. Tier is NOT expressed
      // by disabling this — press it and it tells you what Pro is for.
      disabled={busy}
      onClick={onToggle}
    >
      {busy ? <span className="spinner" /> : '📎'}
    </button>
  );
}

export function ChatAttachPanel({
  open,
  isPro,
  isDemo,
  busy,
  attachment,
  notice,
  otherName,
  onFile,
  onClear,
  onDismissNotice,
}: {
  open: boolean;
  isPro: boolean;
  /** Demo mode has nowhere to store a file, so it says so instead of offering one. */
  isDemo: boolean;
  busy: boolean;
  attachment: ChatAttachment | null;
  /** A refusal from the client pipeline — shown verbatim; it names the limit. */
  notice: string;
  /** Whose screen the attachment has to open on. */
  otherName: string;
  onFile(file: File, kind: 'image' | 'video'): void;
  onClear(): void;
  onDismissNotice(): void;
}) {
  const { t } = useI18n();
  const imageInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  if (!open && !attachment && !notice) return null;

  const seconds = VIDEO_MAX_MS / 1000;

  /** Reset the value first: picking the same file twice fires no change event. */
  const take = (e: React.ChangeEvent<HTMLInputElement>, kind: 'image' | 'video') => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onFile(file, kind);
  };

  return (
    // No bottom margin: the composer is a flex column with its own 8px gap.
    <div className="col" style={{ gap: 8 }}>
      {notice && (
        <div className="banner banner--info small spread" role="status">
          <span>{notice}</span>
          <button className="chip" aria-label={t('common.close')} onClick={onDismissNotice}>
            ✕
          </button>
        </div>
      )}

      {attachment && (
        <div className="row">
          {attachment.previewUrl ? (
            <img
              src={attachment.previewUrl}
              alt=""
              style={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            />
          ) : (
            <span aria-hidden style={{ fontSize: '1.2rem' }}>
              🎬
            </span>
          )}
          <div className="tiny muted" style={{ flex: 1, minWidth: 0 }}>
            {attachment.prepared.kind === 'video'
              ? t('msg.media.readyVideo', {
                  seconds: Math.max(1, Math.round((attachment.prepared.durationMs ?? 0) / 1000)),
                  size: Math.round(attachment.prepared.bytes / 1000),
                })
              : t('msg.media.readyPhoto', { size: Math.round(attachment.prepared.bytes / 1000) })}
          </div>
          <button className="chip" disabled={busy} onClick={onClear}>
            {t('msg.media.remove')}
          </button>
        </div>
      )}

      {open && isDemo && <div className="tiny faint">{t('msg.media.demo')}</div>}

      {open && !isDemo && !isPro && (
        // NO FILE PICKER IN THIS BRANCH. The requirement is stated where the
        // picker would have been, before anything has been chosen.
        <div
          className="row"
          style={{
            padding: 10,
            borderRadius: 'var(--radius-sm)',
            border: '1px dashed var(--border-strong)',
            alignItems: 'flex-start',
          }}
        >
          <span aria-hidden style={{ fontSize: '1rem' }}>
            🔒
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="tiny" style={{ lineHeight: 1.5 }}>
              {t('msg.media.proOnly')}
            </div>
            <Link
              to="/profile/subscription"
              className="tiny"
              style={{ display: 'inline-block', marginTop: 6, color: 'var(--accent-ink)' }}
            >
              {t('pro.upgrade')} →
            </Link>
          </div>
        </div>
      )}

      {open && !isDemo && isPro && (
        <div className="col" style={{ gap: 6 }}>
          <input
            ref={imageInput}
            type="file"
            accept={MEDIA_IMAGE_CONTENT_TYPES.join(',')}
            hidden
            onChange={(e) => take(e, 'image')}
          />
          <input
            ref={videoInput}
            type="file"
            accept={MEDIA_VIDEO_CONTENT_TYPES.join(',')}
            hidden
            onChange={(e) => take(e, 'video')}
          />
          <div className="row wrap">
            <button className="chip" disabled={busy} onClick={() => imageInput.current?.click()}>
              {t('msg.media.photo')}
            </button>
            <button className="chip" disabled={busy} onClick={() => videoInput.current?.click()}>
              {t('msg.media.video')}
            </button>
          </div>
          <div className="tiny faint" style={{ lineHeight: 1.5 }}>
            {t('msg.media.photoNote')}
          </div>
          <div className="tiny faint" style={{ lineHeight: 1.5 }}>
            {t('msg.media.videoNote', { seconds })}
          </div>
          {/* Rule 4, said early. The certain answer arrives from the server the
              moment the message is sent; this is the warning that comes first. */}
          <div className="tiny gold" style={{ lineHeight: 1.5 }}>
            {t('msg.media.viewerNote', { name: otherName })}
          </div>
        </div>
      )}
    </div>
  );
}
